import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Label } from 'app/components/ui/label';
import { Textarea } from 'app/components/ui/textarea';
import { Button } from 'app/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Code, CheckCircle, AlertTriangle, Save } from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface CustomSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public for all keys
const createCustomSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 1 for all custom settings
  const settingsKeys = ['custom_css', 'header_scripts', 'footer_scripts'];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 1,
      };
    }
  });

  return payload;
};

export default function CustomSettings({ form }: CustomSettingsProps) {
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  // Function to handle settings update with is_public for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createCustomSettingsPayload(formData);

      console.log('Custom settings payload with is_public:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('Custom settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating custom settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  return (
    <>
      {/* Custom CSS */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5" />
            Custom CSS
          </CardTitle>
          <CardDescription>
            Add custom CSS to customize your site's appearance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="custom_css">Custom CSS Code</Label>
            <Textarea
              id="custom_css"
              {...form.register('custom_css')}
              placeholder="/* Add your custom CSS here */"
              rows={12}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              This CSS will be applied to all pages
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Custom JavaScript */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5" />
            Custom JavaScript
          </CardTitle>
          <CardDescription>
            Add custom JavaScript for enhanced functionality.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="header_scripts">Header Scripts</Label>
            <Textarea
              id="header_scripts"
              {...form.register('header_scripts')}
              placeholder="// Add scripts for &lt;head&gt; section"
              rows={8}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              These scripts will be added to the &lt;head&gt; section
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="footer_scripts">Footer Scripts</Label>
            <Textarea
              id="footer_scripts"
              {...form.register('footer_scripts')}
              placeholder="// Add scripts before &lt;/body&gt; tag"
              rows={8}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              These scripts will be added before the closing &lt;/body&gt; tag
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Custom settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save custom settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
